import { HorizontalSliderRender } from "./horizontal-slider-render";
import { HorizontalSliderForm } from "./horizontal-slider-form";

export class HorizontalSliderHandler {
    public static AUTO_ENABLED = false;
    public static AUTO_INTERVAL_MS = 5000;
    public static MANUAL_RESUME_DELAY_MS = 7000;
    public static EDGE_EPSILON = 1;

    private readonly form: HorizontalSliderForm;

    private timerId: ReturnType<typeof setInterval> | null;

    private resumeTimerId: ReturnType<typeof setTimeout> | null;

    private pauseUntil: number;

    private isHovered: boolean;

    private isFocused: boolean;

    private autoDirection: number;

    private readonly prefersReducedMotion: boolean;

    public constructor(form: HorizontalSliderForm) {
        this.form = form;
        this.timerId = null;
        this.resumeTimerId = null;
        this.pauseUntil = 0;
        this.isHovered = false;
        this.isFocused = false;
        this.autoDirection = 1;
        this.prefersReducedMotion = matchMedia("(prefers-reduced-motion: reduce)").matches;
    }

    public bootstrap(): void {
        this.updateUI();
        this.syncAuto();
        this.scheduleBootstrapRechecks();
    }

    public handlePrevClick(_event: Event): void {
        this.pauseAfterManual();
        this.autoDirection = -1;
        this.moveByDirection(-1, { edgeModeOverride: "wrap" });
    }

    public handleNextClick(_event: Event): void {
        this.pauseAfterManual();
        this.autoDirection = 1;
        this.moveByDirection(1, { edgeModeOverride: "wrap" });
    }

    public handleTrackScroll(_event: Event): void {
        this.updateUI();
        this.syncAuto();
    }

    public handleResize(_event: Event): void {
        this.updateUI();
        this.syncAuto();
    }

    public handleMouseEnter(_event: Event): void {
        this.isHovered = true;
        this.syncAuto();
    }

    public handleMouseLeave(_event: Event): void {
        this.isHovered = false;
        this.syncAuto();
    }

    public handleFocusIn(_event: FocusEvent): void {
        this.isFocused = true;
        this.syncAuto();
    }

    public handleFocusOut(_event: FocusEvent): void {
        this.isFocused = this.form.hasFocusedDescendant();
        this.syncAuto();
    }

    public handleManualInteraction(_event: Event): void {
        this.pauseAfterManual();
    }

    public handleTrackContentLoad(_event: Event): void {
        this.updateUI();
        this.syncAuto();
    }

    public handleDotClick(dot: HTMLButtonElement): void {
        if (!(dot instanceof HTMLButtonElement)) return;

        const index = Number.parseInt(dot.dataset.index || "", 10);
        if (!Number.isInteger(index) || index < 0) return;

        const slides = this.form.getSlides();
        const track = this.form.getTrack();
        if (!(track instanceof HTMLElement)) return;

        const visibleCount = this.getVisibleSlideCount(track, slides);
        const maxStartIndex = Math.max(slides.length - visibleCount, 0);
        const startIndex = Math.min(index, maxStartIndex);
        const slide = slides[startIndex];
        if (!(slide instanceof HTMLElement)) return;

        this.pauseAfterManual();
        this.scrollToSlide(slide);
    }

    public handleAutoTick(): void {
        if (!this.canAuto()) {
            this.stopAuto();
            return;
        }

        this.autoStep();
    }

    public pauseAfterManual(): void {
        this.pauseUntil = Date.now() + HorizontalSliderHandler.MANUAL_RESUME_DELAY_MS;
        this.scheduleResume();
        this.syncAuto();
    }

    public scheduleResume(): void {
        if (this.resumeTimerId !== null) {
            clearTimeout(this.resumeTimerId);
            this.resumeTimerId = null;
        }

        const delay = Math.max(this.pauseUntil - Date.now(), 0);
        this.resumeTimerId = setTimeout(() => {
            this.resumeTimerId = null;
            this.syncAuto();
        }, delay);
    }

    public scrollByStep(direction: number): void {
        this.moveByDirection(direction, { edgeModeOverride: "wrap" });
    }

    public moveByDirection(direction: number, options: { edgeModeOverride?: "wrap" | "rotate" } = {}): void {
        const track = this.form.getTrack();
        if (!(track instanceof HTMLElement)) return;
        const edgeMode = options.edgeModeOverride || this.form.getEdgeMode();

        const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
        if (maxScrollLeft <= HorizontalSliderHandler.EDGE_EPSILON) return;

        const current = track.scrollLeft;
        const atStart = current <= HorizontalSliderHandler.EDGE_EPSILON;
        const atEnd = current >= maxScrollLeft - HorizontalSliderHandler.EDGE_EPSILON;

        if (edgeMode === "wrap") {
            if (direction > 0 && atEnd) {
                track.scrollTo({ left: 0, behavior: "smooth" });
                return;
            }

            if (direction < 0 && atStart) {
                track.scrollTo({ left: maxScrollLeft, behavior: "smooth" });
                return;
            }
        }

        if (edgeMode === "rotate") {
            if (direction > 0 && atEnd) {
                direction = -1;
                this.autoDirection = -1;
            } else if (direction < 0 && atStart) {
                direction = 1;
                this.autoDirection = 1;
            }
        }

        const step = this.getStep(track) * direction;
        track.scrollBy({
            left: step,
            behavior: "smooth",
        });
    }

    public scrollToSlide(slide: HTMLElement): void {
        const track = this.form.getTrack();
        if (!(track instanceof HTMLElement)) return;

        track.scrollTo({
            left: slide.offsetLeft,
            behavior: "smooth",
        });
    }

    public updateUI(): void {
        const root = this.form.getRoot();
        const track = this.form.getTrack();
        const prevButton = this.form.getPrevButton();
        const nextButton = this.form.getNextButton();
        const dotsContainer = this.form.getDotsContainer();
        const slides = this.form.getSlides();
        const slideCount = slides.length;

        if (!(track instanceof HTMLElement)) return;
        if (!(prevButton instanceof HTMLButtonElement)) return;
        if (!(nextButton instanceof HTMLButtonElement)) return;

        root.classList.toggle("is-empty", slideCount === 0);
        HorizontalSliderRender.renderSectionVisibility(root, slideCount > 0);

        const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
        const current = track.scrollLeft;
        const edgeMode = this.form.getEdgeMode();
        const canWrap = edgeMode === "wrap" || edgeMode === "rotate";

        HorizontalSliderRender.renderControls(root, prevButton, nextButton, {
            atStart: current <= 1,
            atEnd: current >= maxScrollLeft - 1,
            noOverflow: maxScrollLeft <= 1,
            canWrap,
        });

        if (dotsContainer instanceof HTMLElement) {
            const visibleCount = this.getVisibleSlideCount(track, slides);
            const dotCount = this.getDotCount(slideCount, visibleCount);
            const disableDots = dotCount <= 1;
            const renderDotCount = slideCount > 0 ? Math.max(dotCount, 1) : 0;

            dotsContainer.classList.toggle("is-disabled", disableDots && renderDotCount > 0);
            dotsContainer.setAttribute("aria-hidden", "false");

            HorizontalSliderRender.ensureDots(dotsContainer, renderDotCount);

            if (renderDotCount > 0) {
                const activeIndex = disableDots ? 0 : this.getActiveDotIndex(track, slides, visibleCount);
                HorizontalSliderRender.renderActiveDot(dotsContainer, activeIndex);
                HorizontalSliderRender.renderDotsInteractivity(dotsContainer, disableDots);
            }
        }
    }

    public syncAuto(): void {
        if (!this.canAuto()) {
            this.stopAuto();
            return;
        }

        if (this.timerId !== null) return;

        this.timerId = setInterval(() => {
            this.handleAutoTick();
        }, HorizontalSliderHandler.AUTO_INTERVAL_MS);
    }

    public stopAuto(): void {
        if (this.timerId === null) return;
        clearInterval(this.timerId);
        this.timerId = null;
    }

    public canAuto(): boolean {
        if (!HorizontalSliderHandler.AUTO_ENABLED) return false;
        if (this.prefersReducedMotion) return false;
        if (this.isHovered || this.isFocused) return false;
        if (Date.now() < this.pauseUntil) return false;

        const track = this.form.getTrack();
        if (!(track instanceof HTMLElement)) return false;

        const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
        return maxScrollLeft > 1;
    }

    public autoStep(): void {
        this.moveByDirection(this.autoDirection);
    }

    public scheduleBootstrapRechecks(): void {
        const delays = [250, 1000, 2500];
        delays.forEach((delay) => {
            setTimeout(() => {
                this.updateUI();
                this.syncAuto();
            }, delay);
        });
    }

    public getStep(track: HTMLElement): number {
        const firstSlide = this.form.getSlides()[0] || null;
        if (firstSlide instanceof HTMLElement) {
            const styles = getComputedStyle(track);
            const gapRaw = styles.columnGap || styles.gap || "0";
            const gap = Number.parseFloat(gapRaw) || 0;
            return Math.max(firstSlide.getBoundingClientRect().width + gap, 180);
        }

        return Math.max(track.clientWidth * 0.8, 180);
    }

    public getDotCount(slideCount: number, visibleCount: number): number {
        if (slideCount <= 0) return 0;
        const safeVisible = Math.max(visibleCount, 1);
        return Math.max(slideCount - safeVisible + 1, 1);
    }

    public getVisibleSlideCount(track: HTMLElement, slides: HTMLElement[]): number {
        if (slides.length <= 0) return 1;

        const firstSlide = slides[0];
        if (!(firstSlide instanceof HTMLElement)) return 1;

        const styles = getComputedStyle(track);
        const gapRaw = styles.columnGap || styles.gap || "0";
        const gap = Number.parseFloat(gapRaw) || 0;
        const slideWidth = firstSlide.getBoundingClientRect().width;
        if (slideWidth <= 0) return 1;

        const rawVisible = (track.clientWidth + gap) / (slideWidth + gap);
        const visible = Math.ceil(rawVisible);
        return Math.max(visible, 1);
    }

    public getActiveDotIndex(track: HTMLElement, slides: HTMLElement[], visibleCount: number): number {
        if (slides.length === 0) return 0;

        const maxStartIndex = Math.max(slides.length - Math.max(visibleCount, 1), 0);
        if (maxStartIndex <= 0) return 0;

        const maxScrollLeft = Math.max(track.scrollWidth - track.clientWidth, 0);
        if (maxScrollLeft <= HorizontalSliderHandler.EDGE_EPSILON) return 0;

        const progress = Math.min(Math.max(track.scrollLeft / maxScrollLeft, 0), 1);
        return Math.round(progress * maxStartIndex);
    }
}
